import {
  LoanClosedReasonCode,
  Maybe,
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '@possible/cassandra/src/types/consumer'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {AppEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import {ShowException} from 'src/lib/errors'
import i18n from 'src/lib/localization/i18n'
import {PushWebPageOnStack} from 'src/navigation/NavHelper'
import {possibleHowSDBRestrictionsWork, possibleWhyRejected} from 'src/navigation/WebLinks'
import * as DashboardLoanUtils from 'src/products/loans/Dashboard/DashboardLoanUtils/DashboardLoan.utils'
import {DashboardNavigation} from 'src/products/loans/Dashboard/DashboardLoanUtils/DashboardLoan.utils'
import {
  ClosedLoanDashboardAggregateStatusQueryHookResult,
  DashboardLoanAggregateStatusQueryLoanOfferInfo,
} from 'src/products/loans/Dashboard/DashboardLoan/queries/useDashboardLoanAggregateStatusQuery'
import {
  DashboardLoanClosedLoanDisbursementFields,
  DashboardLoanClosedLoanPaymentFields,
  DashboardLoanClosedProps,
} from 'src/products/loans/Dashboard/DashboardLoanClosed/DashboardLoanClosed.types'
import {usePfDispatch} from 'src/store/utils'
import {LinkedAccount, LinkedAccountsSelector} from 'src/lib/bank/useGetLinkedAccounts.utils'
import {UseCassandraQuerySelectedDataType} from '@possible/cassandra/src/utils/hooks'

/**
 * Allow a user to pay off their overdue membership subscription amount.
 */
export const onMakeAPayment = (params: {navigation: DashboardNavigation}): void => {
  const {navigation} = params
  TrackAppEvent(
    ManageActiveLoanEvents.pay_overdue_subscription_amount,
    AppEvents.Category.ManageSubscription,
  )
  navigation.push('LoanPay')
}
/**
 * Constructs complete props for DashboardLoanClosed given GraphQL response data for DashboardLoanAggregateStatusQuery.
 */
export const getDashboardLoanClosedProps = (config: {
  navigation: DashboardNavigation
  closedLoanDashboardData: Pick<
    ClosedLoanDashboardAggregateStatusQueryHookResult,
    'closedLoanAggregateStatus' | 'userProfile' | 'loanTypeVariant' | 'subscriptions'
  >
  dispatch: ReturnType<typeof usePfDispatch>
  shouldRedirectLoanApplyAndAcceptToWeb: boolean
  loanOffers: DashboardLoanAggregateStatusQueryLoanOfferInfo[]
  isAdvance: boolean
  contUwAmount?: string
  contUwAssessmentDate?: string
  linkedAccountsData: UseCassandraQuerySelectedDataType<typeof LinkedAccountsSelector> | undefined
  onResumeMembership: () => Promise<void>
  isResumeMembershipLoading: boolean
  onAcceptSubscriptionAgreement: () => Promise<void>
  refetchDashboardData: () => Promise<void>
}): DashboardLoanClosedProps => {
  const {
    closedLoanDashboardData,
    navigation,
    dispatch,
    shouldRedirectLoanApplyAndAcceptToWeb,
    loanOffers,
    isAdvance,
    contUwAmount,
    contUwAssessmentDate,
    linkedAccountsData,
    onResumeMembership,
    isResumeMembershipLoading,
    onAcceptSubscriptionAgreement,
    refetchDashboardData,
  } = config
  const {closedLoanAggregateStatus, userProfile} = closedLoanDashboardData

  const payments: DashboardLoanClosedLoanPaymentFields[] =
    closedLoanAggregateStatus.closedPayments?.payments?.map(
      (thisPayment: DashboardLoanClosedLoanPaymentFields) => {
        return {
          id: thisPayment.id,
          statusCode: thisPayment.statusCode,
          amount: thisPayment.amount,
          originalDate: thisPayment.originalDate,
          rescheduledDate: thisPayment.rescheduledDate,
          ordinal: thisPayment.ordinal,
          methodCode: thisPayment.methodCode,
          lastTransaction: thisPayment.lastTransaction,
        }
      },
    )

  const disbursement: DashboardLoanClosedLoanDisbursementFields | undefined =
    closedLoanAggregateStatus.closedDisbursement || undefined

  const loanCount = closedLoanAggregateStatus.closedLoanCount
  const hasPreviousLoan = loanCount > 1

  const props: DashboardLoanClosedProps = {
    loanOffers,
    payments,
    disbursement,
    prequalAmount: contUwAmount ?? closedLoanAggregateStatus.prequalAmount,
    reason: closedLoanAggregateStatus.reason,
    reapplyOn: closedLoanAggregateStatus.reapplyOn,
    reapplyOnDays: closedLoanAggregateStatus.reapplyOnDays,
    userEmailAddress: userProfile?.email?.address || '',
    hasPreviousLoan,
    loanTypeVariant: closedLoanDashboardData.loanTypeVariant,
    subscriptions: closedLoanDashboardData.subscriptions,
    isAdvance,
    linkedAccountsData,
    onApplyForNewLoan: async ({sourceForAnalytics} = {}): Promise<void> => {
      const isAfterReapplyOnDate = isReapplyOnDatePassed(closedLoanAggregateStatus.reapplyOn)
      if (!isAfterReapplyOnDate) {
        // this shouldn't be called if its before the reapplyOn date but we check just in case
        DashboardLoanUtils.loanDashboardLogError(
          new Error("Can't re-apply to a new loan yet"),
          `DashboardLoanClosed onApplyForNewLoan() called but its not after the reapplyOn date yet, reason=${closedLoanAggregateStatus.reason} reapplyOn=${closedLoanAggregateStatus.reapplyOn}, prequalAmount=${closedLoanAggregateStatus.prequalAmount}`,
        )
        ShowException(
          i18n.t('DashboardLoan:FeelFreeToApplyAgainOn', {
            date: new Date(closedLoanAggregateStatus.reapplyOn).toDateString(),
          }),
        )
        return
      }
      await DashboardLoanUtils.onReapplyForNewLoan({
        navigation,
        dispatch,
        shouldRedirectLoanApplyAndAcceptToWeb,
        loanOffers,
        reapplyEventArgs: {
          reason: closedLoanAggregateStatus.reason,
          source: sourceForAnalytics,
          reapplyOnDate: closedLoanAggregateStatus.reapplyOn,
          currentDate: new Date().toISOString(),
        },
      })
    },
    // onShowDefaultExplanation() and onShowChargedOffExplanation() are used by LoanProgressTile
    // but they're never used on the closed dashboard since we only show LoanProgressTile when loan is closed paid off,
    // not default or charged off, so we can leave them as a no op
    onShowDefaultExplanation: (): void => {
      DashboardLoanUtils.loanDashboardLogError(
        new Error(
          'DashboardLoanClosed onShowDefaultExplanation() called when it should not be possible',
        ),
      )
    },
    onShowChargedOffExplanation: (): void => {
      DashboardLoanUtils.loanDashboardLogError(
        new Error(
          'DashboardLoanClosed onShowChargedOffExplanation() called when it should not be possible',
        ),
      )
    },
    onContactUs: () => DashboardLoanUtils.onContactUs({navigation}),
    onLoanRejectedLearnMore: () => onLoanRejectedLearnMore({navigation}),
    onViewStateDatabasePolicy: () => onViewStateDatabasePolicy({navigation}),
    onViewLoanHistory: (): void => {
      DashboardLoanUtils.onViewLoanHistory({navigation})
    },
    contUwAssessmentDate,
    onResumeMembership,
    isResumeMembershipLoading,
    onAcceptSubscriptionAgreement,
    refetchDashboardData,
    onMakeAPayment: () => {
      void onMakeAPayment({
        navigation,
      })
    },
  }
  return props
}

export const onLoanRejectedLearnMore = (params: {navigation: DashboardNavigation}): void => {
  TrackAppEvent(
    ManageActiveLoanEvents.rejected_explanation_selected,
    AppEvents.Category.ManageActiveLoan,
  )
  PushWebPageOnStack(params.navigation, {uri: possibleWhyRejected})
}
export const onViewStateDatabasePolicy = (params: {navigation: DashboardNavigation}): void => {
  TrackAppEvent(
    ManageActiveLoanEvents.state_db_explanation_selected,
    AppEvents.Category.ManageActiveLoan,
  )
  PushWebPageOnStack(params.navigation, {uri: possibleHowSDBRestrictionsWork})
}

/**
 * This function is used to determine if the reapplyOn date has passed based on the current date.
 */
export const isReapplyOnDatePassed = (reapplyOn: string): boolean => {
  const reapplyOnDate = new Date(reapplyOn)
  const currentDate = new Date()
  return currentDate > reapplyOnDate
}

/**
 * This function is used to determine if any of the eligibility tiles should be shown.
 * It returns true if the reason is not one of the excluded reasons.
 */
export const shouldShowEligibleTiles = (reason: LoanClosedReasonCode): boolean => {
  const excludedReasons: LoanClosedReasonCode[] = [
    LoanClosedReasonCode.ChargedoffSettled,
    LoanClosedReasonCode.LoanExpired,
    LoanClosedReasonCode.DecisioningExpired,
    LoanClosedReasonCode.Cancelled,
  ]
  return !excludedReasons.includes(reason)
}

/**
 * This function is used to determine if the MembershipOverdueTile should be shown for Cash Advance users.
 * Cash Advance users are the only ones who should see this tile if they have a overdue membership subscription
 */
export const shouldShowMembershipOverdueTile = ({
  isAdvance,
  subscriptions,
}: {
  isAdvance: boolean
  subscriptions: Pick<DashboardLoanClosedProps, 'subscriptions'>['subscriptions']
}): boolean => {
  const subscriptionOverdueAmount = parseFloat(
    subscriptions?.current?.payments.overdueAmount ?? '0.00',
  )

  return isAdvance === true && subscriptionOverdueAmount > 0
}

/**
 * This function is used to determine if the MembershipCancelledTile should be shown for Cash Advance users.
 * Cash Advance users are the only ones who should see this tile if they have a cancelled subscription
 */
export const shouldShowMembershipCancelledTile = ({
  isAdvance,
  subscriptions,
}: {
  isAdvance: boolean
  subscriptions: Pick<DashboardLoanClosedProps, 'subscriptions'>['subscriptions']
}): boolean => {
  const hasMembershipOverdue =
    parseFloat(subscriptions?.current?.payments.overdueAmount ?? '0.00') > 0
  const isMembershipCancelled =
    subscriptions?.current?.statusData.status === SubscriptionStatus.Cancelled

  const isPendingCancellation =
    subscriptions?.current?.statusData.status === SubscriptionStatus.Active &&
    subscriptions.current.statusData.substatus === SubscriptionSubstatus.ActivePendingCancellation

  return (
    isAdvance === true && (isMembershipCancelled || isPendingCancellation) && !hasMembershipOverdue
  )
}

/**
 * This function is used to determine if the LinkAccountTile should be shown.
 * Cash Advance users are the only ones who should see this tile if they don't have have a preferred account
 * or if they have a preferred account that requires re-login
 */
export const shouldShowLinkAccountTile = ({
  isAdvance,
  preferredAccount,
}: {
  isAdvance: boolean
  preferredAccount: Maybe<LinkedAccount> | undefined
}): boolean => {
  return isAdvance === true && (!preferredAccount || preferredAccount?.isLoginRequired === true)
}

/**
 * This function is used to determine if the AdvancePendingTile should be shown.
 * Cash Advance users are the only ones who should see this tile if they have a paid off loan and it's before the reapplyOn date
 */
export const shouldShowAdvancePendingTile = ({
  reason,
  isAdvance,
  isAfterReapplyOnDate,
}: {
  reason: LoanClosedReasonCode
  isAdvance: boolean
  isAfterReapplyOnDate: boolean
}): boolean => {
  if (isAdvance === false) {
    return false
  }

  return reason === LoanClosedReasonCode.Paidoff && !isAfterReapplyOnDate
}

/**
 * This function is used to determine if the NextAdvanceTile should be shown.
 * Cash Advance users are the only ones who should see this tile if they have a prequal amount greater than 0,
 * it's after the reapplyOn date, and they are eligible based on the close reason.
 */
export const shouldShowNextAdvanceTile = ({
  reason,
  isAdvance,
  prequalAmount,
  isAfterReapplyOnDate,
  subscriptions,
}: {
  reason: LoanClosedReasonCode
  isAdvance: boolean
  prequalAmount: Maybe<string> | undefined
  isAfterReapplyOnDate: boolean
  subscriptions: Pick<DashboardLoanClosedProps, 'subscriptions'>['subscriptions']
}): boolean => {
  if (
    isAdvance === false ||
    !prequalAmount ||
    !shouldShowEligibleTiles(reason) ||
    shouldShowMembershipCancelledTile({isAdvance, subscriptions}) ||
    shouldShowMembershipOverdueTile({isAdvance, subscriptions})
  ) {
    return false
  }

  const prequalAmountValue = parseFloat(prequalAmount)
  return prequalAmountValue > 0 && isAfterReapplyOnDate
}

/**
 * This function is used to determine if the LoanPaidOffTile should be shown.
 * Loan users are the only ones who should see this tile if:
 * - they are eligible based on the close reason
 * - loan is paid off or charged off
 * - they don't have a prequal amount OR have a prequal amount equal to 0 OR it's before the reapplyOn date
 */
export const shouldShowLoanPaidOffTile = ({
  reason,
  isAdvance,
  prequalAmount,
  isAfterReapplyOnDate,
}: {
  reason: LoanClosedReasonCode
  isAdvance: boolean
  prequalAmount: Maybe<string> | undefined
  isAfterReapplyOnDate: boolean
}): boolean => {
  if (!shouldShowEligibleTiles(reason)) {
    return false
  }

  const isPaidoffOrChargedoffPaidoff =
    reason === LoanClosedReasonCode.Paidoff || reason === LoanClosedReasonCode.ChargedoffPaidoff
  const hasPrequalCondition =
    !prequalAmount || parseFloat(prequalAmount) === 0 || !isAfterReapplyOnDate

  return isAdvance === false && isPaidoffOrChargedoffPaidoff && hasPrequalCondition
}

/**
 * This function is used to determine if the LoanEligibleToApplyTile should be shown.
 * Both Loans and Cash Advance users should see this tile under the following conditions:
 * - they are eligible based on the close reason
 * - Loans
 *   - Loan is paid off, charged off paid off, rejected, or unmet state requirement and it's after the reapplyOn date
 *   - Not showing LoanPaidOffTile (to avoid showing both tiles simultaneously)
 * - Cash Advance
 *   - Subscription is active and
 *   - Cash Advance is paid off, charged off paid off, rejected, or unmet state requirement and it's after the reapplyOn date and
 *   - Prequal amount doesn't exist or it's equal to 0
 *   - Not showing NextAdvanceTile (to avoid showing both tiles simultaneously)
 */
export const shouldShowLoanEligibleToApplyTile = ({
  reason,
  isAdvance,
  isAfterReapplyOnDate,
  prequalAmount,
  subscriptions,
  preferredAccount,
}: {
  reason: LoanClosedReasonCode
  isAdvance: boolean
  isAfterReapplyOnDate: boolean
  prequalAmount: Maybe<string> | undefined
  subscriptions: Pick<DashboardLoanClosedProps, 'subscriptions'>['subscriptions']
  preferredAccount: Maybe<LinkedAccount> | undefined
}): boolean => {
  if (!shouldShowEligibleTiles(reason)) {
    return false
  }

  const isRejectedOrUnmetStateOrPaidoffOrChargedoffPaidoff =
    reason === LoanClosedReasonCode.Rejected ||
    reason === LoanClosedReasonCode.UnmetStateRequirement ||
    reason === LoanClosedReasonCode.Paidoff ||
    reason === LoanClosedReasonCode.ChargedoffPaidoff

  const canReapplyAfterDateAndReason =
    isAfterReapplyOnDate && isRejectedOrUnmetStateOrPaidoffOrChargedoffPaidoff

  if (!canReapplyAfterDateAndReason) {
    return false
  }

  if (isAdvance) {
    const isSubscriptionActive =
      subscriptions?.current?.statusData.status !== SubscriptionStatus.Cancelled &&
      subscriptions?.current?.statusData.substatus !==
        SubscriptionSubstatus.ActivePendingCancellation &&
      !shouldShowMembershipOverdueTile({isAdvance, subscriptions})
    const prequalAmountValue = prequalAmount ? parseFloat(prequalAmount) : 0
    // For Cash Advance: show if subscription active with no overdue amount, no prequalAmount or zero prequalAmount, and NextAdvanceTile or LinkAccountTile is not showing
    return (
      isSubscriptionActive &&
      (!prequalAmount || prequalAmountValue === 0) &&
      !shouldShowNextAdvanceTile({
        isAdvance,
        prequalAmount,
        isAfterReapplyOnDate,
        reason,
        subscriptions,
      }) &&
      !shouldShowLinkAccountTile({isAdvance, preferredAccount})
    )
  }

  // For Loans: do not show this tile if LoanPaidOffTile is already showing
  return !shouldShowLoanPaidOffTile({isAdvance, prequalAmount, isAfterReapplyOnDate, reason})
}
